import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import { serviceTicketsApi } from '../api/serviceTickets';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';
import ContactPicker from '../components/ContactPicker';
import TicketDetail from './TicketDetail';

const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', purple:'badge-purple', red:'badge-red' };

const ServiceTickets: React.FC = () => {
  const { showToast } = useToast();
  const [mode, setMode]         = useState<'list'|'new'>('list');
  const [tickets, setTickets]   = useState<any[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId]     = useState<number|null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [saving, setSaving]     = useState(false);

  const [form, setForm] = useState<any>({
    contact_id:'', branch_id:'', device_type:'Phone', device_brand:'', device_model:'',
    device_serial:'', unlock_code:'', condition_on_intake:'', accessories:'',
    problem_description:'', estimated_cost:'', promised_date:'',
    received_date: new Date().toISOString().split('T')[0],
  });

  const load = useCallback(() => {
    setLoading(true);
    serviceTicketsApi.list({ search, status: statusFilter || undefined, limit: 100 })
      .then(r => { setTickets(r.data.data || []); setTotal(r.data.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { purchaseApi.branches().then(r => setBranches(r.data.data || [])).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const handleCreate = async () => {
    if (!form.contact_id) { showToast('Customer required', 'error'); return; }
    if (!form.branch_id)  { showToast('Branch required', 'error'); return; }
    if (!form.problem_description) { showToast('Reported fault required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...form, contact_id: Number(form.contact_id), branch_id: Number(form.branch_id),
        estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : undefined,
        promised_date: form.promised_date || undefined };
      const res = await serviceTicketsApi.create(payload);
      showToast(res.data.message);
      setMode('list');
      setForm({ contact_id:'', branch_id:'', device_type:'Phone', device_brand:'', device_model:'',
        device_serial:'', unlock_code:'', condition_on_intake:'', accessories:'',
        problem_description:'', estimated_cost:'', promised_date:'',
        received_date: new Date().toISOString().split('T')[0] });
      load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  if (selectedId) return <TicketDetail ticketId={selectedId} onBack={() => { setSelectedId(null); load(); }} />;

  if (mode === 'new') return (
    <div>
      <div className="page-header">
        <h1 className="page-title">New Service Ticket</h1>
        <button className="btn btn-secondary" onClick={() => setMode('list')}>Cancel</button>
      </div>
      <div className="card">
        <div className="card-body">
          <div style={{fontSize:12,fontWeight:600,marginBottom:8}}>Customer &amp; device</div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">Customer *</label>
              <ContactPicker value={form.contact_id} placeholder="Type to search" onSelect={(c:any) => setForm((f:any)=>({...f,contact_id:String(c.id)}))} />
            </div>
            <div className="form-group"><label className="form-label">Branch *</label>
              <select className="form-control" value={form.branch_id} onChange={e => setForm((f:any)=>({...f,branch_id:e.target.value}))}>
                <option value="">Select branch</option>
                {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Received</label>
              <input type="date" className="form-control" value={form.received_date} onChange={e => setForm((f:any)=>({...f,received_date:e.target.value}))} />
            </div>
          </div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">Device type</label>
              <input className="form-control" value={form.device_type} onChange={e => setForm((f:any)=>({...f,device_type:e.target.value}))} placeholder="Phone, Laptop..." />
            </div>
            <div className="form-group"><label className="form-label">Brand</label>
              <input className="form-control" value={form.device_brand} onChange={e => setForm((f:any)=>({...f,device_brand:e.target.value}))} placeholder="Apple, Samsung..." />
            </div>
            <div className="form-group"><label className="form-label">Model</label>
              <input className="form-control" value={form.device_model} onChange={e => setForm((f:any)=>({...f,device_model:e.target.value}))} />
            </div>
          </div>
          <div className="form-grid-3">
            <div className="form-group"><label className="form-label">Serial / IMEI</label>
              <input className="form-control mono" value={form.device_serial} onChange={e => setForm((f:any)=>({...f,device_serial:e.target.value}))} />
            </div>
            <div className="form-group"><label className="form-label">Unlock code</label>
              <input className="form-control" value={form.unlock_code} onChange={e => setForm((f:any)=>({...f,unlock_code:e.target.value}))} />
            </div>
            <div className="form-group"><label className="form-label">Alternate mobile</label>
              <input className="form-control" value={form.alternate_mobile||''} onChange={e => setForm((f:any)=>({...f,alternate_mobile:e.target.value}))} placeholder="If the customer can't be reached" />
            </div>
          </div>
          <div className="form-group"><label className="form-label">Condition on arrival</label>
            <input className="form-control" value={form.condition_on_intake} onChange={e => setForm((f:any)=>({...f,condition_on_intake:e.target.value}))} placeholder="Scratches, cracks, water damage..." />
          </div>
          <div className="form-group"><label className="form-label">Reported fault *</label>
            <textarea className="form-control" rows={3} value={form.problem_description} onChange={e => setForm((f:any)=>({...f,problem_description:e.target.value}))} />
          </div>
          <div className="form-grid-3">

            <div className="form-group"><label className="form-label">Promised date</label>
              <input type="date" className="form-control" value={form.promised_date} onChange={e => setForm((f:any)=>({...f,promised_date:e.target.value}))} />
            </div>
          </div>
          <div style={{marginTop:12,display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button className="btn btn-secondary" onClick={() => setMode('list')}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving...' : 'Create ticket'}</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Service Tickets</h1>
        <button className="btn btn-primary" onClick={() => setMode('new')}><Plus size={13} /> New Ticket</button>
      </div>

      <div className="filter-bar" style={{display:'flex',gap:8,alignItems:'center',marginBottom:12,flexWrap:'wrap'}}>
        <div style={{position:'relative',flex:1,minWidth:200}}>
          <Search size={13} style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'var(--tx3)'}} />
          <input className="form-control" style={{paddingLeft:26}} placeholder="Search ticket, customer, serial..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {['','Open','Quoted','Approved','In Progress','Completed','Delivered','Rejected'].map(s => (
          <button key={s||'all'} className={`btn btn-sm ${statusFilter===s ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setStatusFilter(s)}>{s || 'All'}</button>
        ))}
        <span className="record-count">{total} tickets</span>
      </div>

      <div className="card">
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr style={{background:'var(--sur2)'}}>
            <th style={{padding:'8px 12px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Ticket</th>
            <th style={{padding:'8px 12px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Customer</th>
            <th style={{padding:'8px 12px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Device</th>
            <th style={{padding:'8px 12px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Status</th>
            <th style={{padding:'8px 12px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Received</th>
            <th style={{padding:'8px 12px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Total</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            : tickets.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><p>No tickets yet.</p></div></td></tr>
            : tickets.map((t:any) => (
              <tr key={t.id} onClick={() => setSelectedId(t.id)} style={{cursor:'pointer',borderBottom:'1px solid var(--bdr)'}}>
                <td className="td-code" style={{padding:'9px 12px'}}>{t.ticket_no}</td>
                <td style={{padding:'9px 12px',fontSize:12}}>{t.customer_name}<div style={{fontSize:11,color:'var(--tx3)'}}>{t.customer_mobile}</div></td>
                <td style={{padding:'9px 12px',fontSize:12,color:'var(--tx2)'}}>{[t.device_brand,t.device_model].filter(Boolean).join(' ')}
                  {t.device_serial && <div style={{fontSize:11,color:'var(--tx3)',fontFamily:'monospace'}}>{t.device_serial}</div>}</td>
                <td style={{padding:'9px 12px'}}><span className={`badge ${colorMap[t.status_color]||'badge-gray'}`}>{t.status}</span>
                  {t.on_hold ? <div style={{fontSize:10,color:'var(--red)',marginTop:2}}>On hold</div> : null}</td>
                <td style={{padding:'9px 12px',fontSize:11,color:'var(--tx3)'}}>{t.received_date ? new Date(t.received_date).toLocaleDateString() : '-'}</td>
                <td style={{padding:'9px 12px',textAlign:'right',fontFamily:'monospace',fontSize:12,fontWeight:600}}>{fmt(t.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceTickets;
